import clsx from "clsx";
import type React from "react";
import type { CSSProperties, LegacyRef, MouseEventHandler } from "react";
import {
  cloneElement,
  forwardRef,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState
} from "react";
import { CSSTransition } from "react-transition-group";
import { useHeightObserver } from "../../utils";
import Icon from "../Icon";
import styles from "./index.module.scss";

export interface AccordionsProps {
  multiple?: boolean;
  mandatory?: boolean;
  value?: Array<number>;
  onChange?: (value: Array<number>) => void;
  children?:
    | React.ReactElement<AccordionProps>
    | React.ReactElement<AccordionProps>[];
}
export type AccordionButtonProps = {
  label?: string;
  onClick?: MouseEventHandler | (() => void);
  active?: boolean;
};
export type AccordionProps = {
  ref?: (el: TAccordionRef) => void;
  button?: React.ReactElement<AccordionButtonProps>;
  content?: string;
  children: JSX.Element | string;
  defaultOpen?: boolean;
} & Omit<AccordionButtonProps, "active">;

export type TAccordionRef = {
  id: string;
  open(): void;
  close(): void;
  isOpen(): boolean;
};

export const Accordions = (props: AccordionsProps) => {
  const { children, mandatory, multiple, onChange, value } = props;
  const childRefs = useRef<Record<string, TAccordionRef>>({});
  const emitChanges = () => {
    if (!onChange) return;
    const children = Object.entries(childRefs.current);
    const openChildren = children.filter(
      ([_, value]) => value?.isOpen && value.isOpen()
    );
    onChange(openChildren.map((i) => children.indexOf(i)));
  };
  const closeAll = () => {
    Object.values(childRefs.current).forEach((i) => i?.close && i.close());
  };
  useEffect(() => {
    const children = Object.values(childRefs.current);
    for (let i = 0; i < children.length; i++) {
      const child = children[i];
      if (value?.includes(i) && child.open) child.open();
      else child.close();
    }
  }, [value]);

  const childrenArray = useMemo(
    () =>
      (Array.isArray(children) ? children : children ? [children] : []).filter(
        Boolean
      ),
    [children]
  );
  const regiseredChildren = childrenArray.map((child) => {
    let id: string = "";
    return cloneElement(child, {
      ...child?.props,
      ref: (el) => {
        id = el?.id;
        if (!id) return;
        childRefs.current[id] = el;
      },
      onClick(e) {
        const { isOpen, close, open } = childRefs.current[id];

        if (isOpen()) {
          if (!mandatory) {
            close();
          } else if (
            multiple &&
            Object.values(childRefs.current).find(
              (i) => i?.isOpen && i.isOpen() && i.id !== id
            )
          ) {
            close();
          }
        } else {
          if (!multiple) {
            closeAll();
          }
          open();
        }
        if (child.props.onClick) child.props.onClick(e);
        setTimeout(emitChanges, 0);
      }
    });
  });

  return <>{regiseredChildren}</>;
};

export const AccordionButton = (props: AccordionButtonProps) => {
  const { label, onClick, active } = props;
  return (
    <button
      className={clsx(
        styles["accordion-button"],
        active && styles["accordion-button--open"]
      )}
      onClick={onClick}
    >
      {label}
      <Icon size={1} icon="mi-Arrow-Down" color="neutral-900" />
    </button>
  );
};

const Accordion = forwardRef((props: AccordionProps, ref) => {
  const { label, button, onClick, children, content, defaultOpen } = props;
  const [open, setOpen] = useState(!!defaultOpen);
  useEffect(() => {
    setOpen(!!defaultOpen);
  }, [defaultOpen]);

  const id = useId();
  useImperativeHandle(
    ref,
    () => ({
      id,
      open() {
        setOpen(true);
      },
      close() {
        setOpen(false);
      },
      isOpen() {
        return open;
      }
    }),
    [id, open]
  );

  const accordionButton = cloneElement<AccordionButtonProps>(
    button || <AccordionButton />,
    {
      active: open,
      label,
      onClick
    }
  );

  const contentRef = useRef<HTMLDivElement>(null);
  const height = useHeightObserver(contentRef, [open]);

  return (
    <div
      className={styles["accordion"]}
      style={{ "--height": `${height}px` } as CSSProperties}
      ref={ref as LegacyRef<HTMLDivElement>}
    >
      <div className={styles["accordion__button"]}>{accordionButton}</div>
      <CSSTransition
        in={open}
        unmountOnExit
        classNames={{
          enterDone: styles["accordion__content--enter-done"],
          exit: styles["accordion__content--exit"]
        }}
        timeout={{ enter: 0, exit: 300 }}
      >
        <div ref={contentRef} className={styles["accordion__content"]}>
          {content || children}
        </div>
      </CSSTransition>
    </div>
  );
});

Accordion.displayName = "Accordion";
export default Accordion;
